import { useMemo, useState, type ReactNode } from "react";
import { ActionIcon, Alert, Badge, Box, Card, Chip, CopyButton, Group, HoverCard, SimpleGrid, Skeleton, Stack, Switch, Table, Tabs, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { IconBolt, IconBrain, IconCheck, IconCoin, IconCopy, IconInfoCircle, IconSearch, IconUsersGroup } from "@tabler/icons-react";
import type { ModelPick, ModelPickKind } from "@passio/easyai-client";
import { PageHeader } from "../theme/components";
import { CodeBlock } from "../components/JsonView";
import { CostMeter, PickBadge, RatingDots, TierBadge } from "../components/ModelPicker";
import { ErrorNotice } from "../components/common";
import { publicApiBase } from "../lib/clients";
import { COST_LEVELS, useModelInfo, type ModelInfo } from "../lib/modelInfo";
import { aiRoute } from "../lib/models";
import { fmtAccuracy, fmtSeconds, PICK_KINDS, PICKS, useRouteTests } from "../lib/routeTests";

/** What each tier is for, in this API's terms. */
const GOOD_FOR: Record<string, string> = {
  frontier: "Back-office review, hard recipe parsing and multi-step advisor questions, where one wrong answer costs more than the call.",
  pro: "Messy multi-item plates, vague free text, nutrition advice and anything users will scrutinise.",
  fast: "Everyday photo and text logging. Most routes default to a model in this tier.",
  lite: "Search terms, high-volume text logging and simple suggestions, at the lowest cost per call.",
};

const fmtContext = (n?: number) => (!n ? null : n >= 1_000_000 ? `${+(n / 1_048_576).toFixed(1)}M` : `${Math.round(n / 1000)}K`);
function TagCopy({ tag }: { tag: string }) {
  return (
    <Group gap={2} wrap="nowrap">
      <Text fz={12} ff="monospace" c="dimmed" truncate>
        {tag}
      </Text>
      <CopyButton value={tag}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? "Copied" : "Copy tag for ?model="}>
            <ActionIcon size={18} variant="subtle" color={copied ? "lime.8" : "gray"} onClick={copy} aria-label={`Copy ${tag}`}>
              {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

function Rating({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <Group gap={6} wrap="nowrap" justify="space-between">
      <Group gap={5} wrap="nowrap">
        {icon}
        <Text fz={12} c="dimmed">
          {label}
        </Text>
      </Group>
      {children}
    </Group>
  );
}

function SimilarModels({ model, byTag }: { model: ModelInfo; byTag: Map<string, ModelInfo> }) {
  const peers = model.similarTo.map((t) => byTag.get(t)).filter((m): m is ModelInfo => !!m);
  if (!peers.length) return null;
  return (
    <HoverCard width={340} position="top-start" withArrow shadow="md" openDelay={100}>
      <HoverCard.Target>
        <Group gap={6} wrap="nowrap" style={{ cursor: "help" }}>
          <IconUsersGroup size={14} color="var(--mantine-color-dimmed)" />
          <Text fz={12} c="dimmed" truncate>
            Similar to <b>{peers[0]!.name}</b>
            {peers.length > 1 ? ` +${peers.length - 1}` : ""}
          </Text>
        </Group>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Text fz="xs" fw={700} mb={6}>
          Comparable to {model.name}
        </Text>
        <Stack gap={8}>
          {peers.map((p) => (
            <Group key={p.tag} justify="space-between" wrap="nowrap" gap="xs">
              <Stack gap={0} style={{ minWidth: 0 }}>
                <Text fz="sm" fw={600} truncate>
                  {p.name}{" "}
                  <Text span fz={11} c="dimmed">
                    {p.provider}
                  </Text>
                </Text>
                <Text fz={11} ff="monospace" c="dimmed" truncate>
                  {p.tag}
                </Text>
              </Stack>
              <CostMeter value={p.cost} fz={11} withLabel />
            </Group>
          ))}
        </Stack>
        <Text fz={11} c="dimmed" mt={8}>
          Same tier, other providers first. Handy as a fallback or to compare results on your own data.
        </Text>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

function ModelCard({ m, byTag }: { m: ModelInfo; byTag: Map<string, ModelInfo> }) {
  const ctx = fmtContext(m.contextWindow);
  return (
    <Card padding="md" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text fw={750} truncate>
            {m.name}
          </Text>
          <TagCopy tag={m.tag} />
        </Stack>
        <Badge variant="outline" color="gray" size="sm" tt="none" style={{ flexShrink: 0 }}>
          {m.provider}
        </Badge>
      </Group>
      {m.description && (
        <Text fz={12} c="dimmed" lineClamp={2}>
          {m.description}
        </Text>
      )}
      <Stack gap={5} p="xs" style={{ background: "var(--pd-cream)", borderRadius: 8 }}>
        <Rating icon={<IconBrain size={14} color="var(--pd-violet)" />} label="Intelligence">
          <RatingDots value={m.intelligence} label="Intelligence" />
        </Rating>
        <Rating icon={<IconBolt size={14} color="var(--pd-green)" />} label="Speed">
          <RatingDots value={m.speed} color="var(--pd-lime)" label="Speed" />
        </Rating>
        <Rating icon={<IconCoin size={14} color="var(--pd-amber)" />} label="Cost">
          <CostMeter value={m.cost} withLabel />
        </Rating>
      </Stack>
      <Stack gap={3}>
        <Text fz={12} c="dimmed">
          {COST_LEVELS[m.cost]?.summary}
        </Text>
        {ctx && (
          <Text fz={12} c="dimmed">
            {ctx} token context window
          </Text>
        )}
      </Stack>
      <Group gap={4}>
        {!m.reliable && (
          <Tooltip label="Not generally available from the provider yet. Fine to try; Passio tries it last in fallbacks.">
            <Badge size="xs" color="orange" variant="light">
              preview
            </Badge>
          </Tooltip>
        )}
        {m.visionOnly && (
          <Badge size="xs" color="violet" variant="outline">
            image routes only
          </Badge>
        )}
        {m.variantOf && (
          <Tooltip label={`A preset of ${byTag.get(m.variantOf)?.name ?? m.variantOf} with fixed settings, at the same price.`}>
            <Badge size="xs" color="gray" variant="outline">
              preset
            </Badge>
          </Tooltip>
        )}
        {m.zdr && (
          <Tooltip label="Zero data retention: eligible for accounts that require it.">
            <Badge size="xs" color="gray" variant="light">
              ZDR
            </Badge>
          </Tooltip>
        )}
        {m.estimated && (
          <Tooltip label="New to the catalog: tier and ratings are estimated from price until it's rated.">
            <Badge size="xs" color="yellow" variant="light">
              estimated
            </Badge>
          </Tooltip>
        )}
      </Group>
      <Box mt="auto">
        <SimilarModels model={m} byTag={byTag} />
      </Box>
    </Card>
  );
}

function UsageSnippets({ lite, fast }: { lite: string; fast: string }) {
  const base = publicApiBase();
  const ts = `import { LicenseKeyAuth, PassioClient } from "@passio/api-client";

// Route id -> model tag. Routes not listed run their own default model.
const MODELS: Record<string, string> = {
  extractIngredients: "${lite}", // high-volume text logging
  extractIngredientsAutoTyped: "${fast}", // photo logging
};

const passio = new PassioClient({
  baseUrl: "${base}",
  auth: new LicenseKeyAuth({ licenseKey: process.env.PASSIO_KEY! }),
  model: (routeId) => MODELS[routeId],
});

const { data, meta } = await passio.tools.extractIngredients("two eggs and toast");
console.log(meta.modelTag, meta.billedTokens.total); // which model answered, what it cost`;
  const curl = `curl -i -X POST "${base}/napi/tools/extractIngredients?model=${lite}" \\
  -H "Authorization: Bearer $PASSIO_TOKEN" \\
  -H "Passio-ID: $PASSIO_CUSTOMER_ID" \\
  -H "Content-Type: application/json" \\
  -d '{"content": "two eggs and toast"}'

# Response headers include:
# X-Model-Tag: ${lite}
# X-Billed-Tokens-Total: …`;
  const py = `import os, requests

res = requests.post(
    "${base}/napi/tools/extractIngredients",
    params={"model": "${lite}"},  # omit for the route default
    headers={"Authorization": f"Bearer {os.environ['PASSIO_TOKEN']}", "Passio-ID": os.environ["PASSIO_CUSTOMER_ID"]},
    json={"content": "two eggs and toast"},
)
print(res.headers["X-Model-Tag"], res.headers.get("X-Billed-Tokens-Total"))`;
  return (
    <Tabs defaultValue="ts" variant="pills" radius="xl">
      <Tabs.List mb="xs">
        <Tabs.Tab value="ts">TypeScript SDK</Tabs.Tab>
        <Tabs.Tab value="curl">curl</Tabs.Tab>
        <Tabs.Tab value="py">Python</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="ts">
        <CodeBlock code={ts} language="ts" />
      </Tabs.Panel>
      <Tabs.Panel value="curl">
        <CodeBlock code={curl} language="shell" />
      </Tabs.Panel>
      <Tabs.Panel value="py">
        <CodeBlock code={py} language="python" />
      </Tabs.Panel>
    </Tabs>
  );
}

function PickCell({ pick, kind, byTag }: { pick: ModelPick | null; kind: ModelPickKind; byTag: Map<string, ModelInfo> }) {
  if (!pick?.model) return <Text c="dimmed">-</Text>;
  const m = byTag.get(pick.model);
  const stats = [fmtAccuracy(pick.accuracy) && `${fmtAccuracy(pick.accuracy)} accurate`, fmtSeconds(pick.p50_s) && `${fmtSeconds(pick.p50_s)} typical`].filter(Boolean).join(" · ");
  return (
    <Tooltip label={`${PICKS[kind].summary}${pick.why ? ` ${pick.why}` : ""}`} multiline w={280}>
      <Stack gap={2} style={{ cursor: "help" }}>
        <Group gap={6} wrap="nowrap">
          <Text fz="sm" fw={600} truncate>
            {m?.name ?? pick.model}
          </Text>
          {m && <CostMeter value={m.cost} fz={11} />}
        </Group>
        {stats && (
          <Text fz={11} c="dimmed">
            {stats}
          </Text>
        )}
      </Stack>
    </Tooltip>
  );
}

/** Passio's own test results for each route: which model to pick for balance, accuracy or price. */
function TestedRoutes({ byTag }: { byTag: Map<string, ModelInfo> }) {
  const { byRoute } = useRouteTests();
  if (!byRoute.size) return null;
  const rows = [...byRoute].map(([id, t]) => ({ id, title: aiRoute(id)?.title ?? id, t })).sort((a, b) => a.title.localeCompare(b.title));
  return (
    <Card mb="lg">
      <Text fw={700} mb={4}>
        Tested on Passio routes
      </Text>
      <Text fz="sm" c="dimmed" mb="sm">
        Passio runs every model on a fixed set of real photos and text for each route and scores the answers. <b>Recommended</b> is accurate enough for nearly every call with the best balance of speed and cost, <b>High quality</b> is the most accurate, and <b>Budget</b> is the cheapest and quickest that still does the job.
      </Text>
      <Table.ScrollContainer minWidth={760}>
        <Table verticalSpacing={8}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Route</Table.Th>
              {PICK_KINDS.map((k) => (
                <Table.Th key={k}>
                  <PickBadge kind={k} />
                </Table.Th>
              ))}
              <Table.Th w={110}>Tested</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map(({ id, title, t }) => (
              <Table.Tr key={id}>
                <Table.Td>
                  <Text fz="sm" fw={600}>
                    {title}
                  </Text>
                  <Text fz={11} c="dimmed">
                    {t.cases} cases · {t.models.length} models
                  </Text>
                </Table.Td>
                {PICK_KINDS.map((k) => (
                  <Table.Td key={k}>
                    <PickCell pick={t.picks?.[k] ?? null} kind={k} byTag={byTag} />
                  </Table.Td>
                ))}
                <Table.Td>
                  <Text fz={12} c="dimmed">
                    {new Date(t.tested_at).toLocaleDateString()}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  );
}

const pickTag = (models: ModelInfo[], tier: string, fallback: string) => models.find((m) => m.tier === tier && m.reliable && !m.visionOnly)?.tag ?? fallback;

/** Every model the API accepts, grouped by tier, with ratings and cost. */
export function ModelsPage() {
  const { models, byTag, tiers, isPending, error, catalogError } = useModelInfo();
  const [query, setQuery] = useState("");
  const [providers, setProviders] = useState<string[]>([]);
  const [zdrOnly, setZdrOnly] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  const allProviders = useMemo(() => [...new Set(models.map((m) => m.provider))].sort(), [models]);
  const q = query.trim().toLowerCase();
  const shown = models.filter(
    (m) =>
      (!q || `${m.name} ${m.tag} ${m.provider} ${m.description ?? ""}`.toLowerCase().includes(q)) &&
      (!providers.length || providers.includes(m.provider)) &&
      (!zdrOnly || m.zdr) &&
      (showPreview || m.reliable),
  );

  return (
    <>
      <PageHeader
        eyebrow="GET /napi/models/available · ?model=<tag>"
        title="Models"
        description="Every AI route can run on any model below. Pick one per route to tune for cost, speed or accuracy. The list is live from the API and the Easy AI model catalog, so new models show up here as soon as they ship."
      />

      <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md" mb="lg">
        <Card>
          <Text fw={700} mb={4}>
            Choose per call
          </Text>
          <Text fz="sm" c="dimmed">
            Add <code>?model=&lt;tag&gt;</code> to any AI route, or set the SDK's <code>model</code> option once, per route if you like. Leave it out and the route runs the default Passio picked for it. Responses keep the same shape whichever model answers.
          </Text>
        </Card>
        <Card>
          <Text fw={700} mb={4}>
            What you're trading
          </Text>
          <Text fz="sm" c="dimmed">
            Bigger models read messy photos and vague text better, but cost more and take longer. Smaller models are cheap and quick, and they're often good enough for clear input. Try your own samples on two tiers and compare.
          </Text>
        </Card>
        <Card>
          <Text fw={700} mb={4}>
            How cost works
          </Text>
          <Text fz="sm" c="dimmed" mb={8}>
            Most routes run a Lowest-cost model by default. Each step up costs more per call, so use the cheapest model that handles your input well.
          </Text>
          <Stack gap={3}>
            {[1, 2, 3, 4, 5].map((level) => (
              <Tooltip key={level} label={COST_LEVELS[level]!.summary} position="left" multiline w={240}>
                <Box style={{ cursor: "help", width: "fit-content" }}>
                  <CostMeter value={level} withLabel />
                </Box>
              </Tooltip>
            ))}
          </Stack>
          <Text fz="xs" c="dimmed" mt={8}>
            For real numbers, run the same photo or text on two models here: each result shows the tokens that call used.
          </Text>
        </Card>
      </SimpleGrid>

      <Card mb="lg">
        <Text fw={700} mb="xs">
          Set it in code
        </Text>
        <UsageSnippets lite={pickTag(models, "lite", "gemini-3-5-flash-lite")} fast={pickTag(models, "fast", "gemini-3-7-flash")} />
      </Card>

      <TestedRoutes byTag={byTag} />

      {catalogError && (
        <Alert color="yellow" variant="light" icon={<IconInfoCircle size={18} />} mb="md">
          The Easy AI model catalog didn't load ({catalogError.message}), so tiers and ratings are estimated from each model's price.
        </Alert>
      )}
      {error && <ErrorNotice error={error} />}

      <Group gap="md" mb="lg" wrap="wrap" align="center">
        <TextInput placeholder="Search models" leftSection={<IconSearch size={16} />} value={query} onChange={(e) => setQuery(e.currentTarget.value)} w={240} />
        <Chip.Group multiple value={providers} onChange={setProviders}>
          <Group gap={6}>
            {allProviders.map((p) => (
              <Chip key={p} value={p} size="xs" variant="light">
                {p}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
        <Switch size="sm" label="Zero data retention only" checked={zdrOnly} onChange={(e) => setZdrOnly(e.currentTarget.checked)} />
        <Switch size="sm" label="Show preview models" checked={showPreview} onChange={(e) => setShowPreview(e.currentTarget.checked)} />
      </Group>

      {isPending && (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} h={260} radius="md" />
          ))}
        </SimpleGrid>
      )}

      <Stack gap={40}>
        {tiers.map((tier) => {
          const list = shown.filter((m) => m.tier === tier.id);
          if (!list.length) return null;
          return (
            <Box key={tier.id}>
              <Group justify="space-between" align="flex-end" mb="sm" className="pd-rule" pt="md" wrap="wrap">
                <Stack gap={4} maw={760}>
                  <Group gap="xs">
                    <Title order={2} fz={24}>
                      {tier.label}
                    </Title>
                    <TierBadge tier={tier.id} size="sm" />
                    <Text c="dimmed" fz="sm">
                      {list.length} model{list.length === 1 ? "" : "s"}
                    </Text>
                  </Group>
                  <Text c="dimmed" fz="sm">
                    {tier.summary}
                  </Text>
                  {GOOD_FOR[tier.id] && (
                    <Text fz="sm">
                      <b>Good for:</b> {GOOD_FOR[tier.id]}
                    </Text>
                  )}
                </Stack>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                {list.map((m) => (
                  <ModelCard key={m.tag} m={m} byTag={byTag} />
                ))}
              </SimpleGrid>
            </Box>
          );
        })}
        {!isPending && !shown.length && !error && (
          <Text c="dimmed" ta="center">
            No models match those filters.
          </Text>
        )}
      </Stack>

      <Text fz="xs" c="dimmed" mt={40}>
        Ratings run 1 to 5 relative to the other models here. Intelligence and speed are editorial ratings from the Easy AI catalog; cost reflects what a call on that model costs you compared with the others. Image-only presets are offered on photo routes only.
      </Text>
    </>
  );
}
